import { Home, DollarSign, Building2, Landmark, Heart, HelpCircle, Shield, Clock, Scale, Users, MapPin, TrendingUp, FileCheck } from 'lucide-react';

export const sellerSegmentArticles = [
  // ═══════════════════════════════════════════
  // PROPERTY TYPE-SPECIFIC (5 articles)
  // ═══════════════════════════════════════════
  {
    id: 'selling-queenslander-big-block-heritage-development',
    category: 'Seller Guide',
    title: 'Selling a Queenslander on a Big Block: Heritage vs Development Value',
    excerpt: 'Your character Queenslander may be charming, but the land underneath it could be worth far more. How to navigate the heritage question.',
    date: '2026-04-12',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=85',
    icon: Home,
    metaDescription: 'Selling a Queenslander on a big block in Brisbane. How to weigh heritage charm against development value, character overlay rules, and strategies to maximise your sale price.',
    content: `
## The Queenslander Dilemma

Brisbane's iconic Queenslander homes sit on some of the city's most valuable land. A pre-1947 timber home on an 800m² block in Paddington, Red Hill, or Clayfield presents a genuine tension: the house has character value, but the land has development value. Which is worth more?

## Understanding Character Overlays

### Where Character Overlays Apply

Brisbane City Council's Character Overlay affects properties in select suburbs, primarily inner-city areas where pre-1947 homes predominate:

**Key character suburbs:** Paddington, Red Hill, Bardon, Auchenflower, Kelvin Grove, Clayfield, Ascot, Hamilton, Hawthorne, Bulimba, Coorparoo (parts), Greenslopes (parts)

### What the Overlay Means

- **Pre-1947 homes** cannot be demolished without a formal assessment
- **New development** must be sympathetic to the existing streetscape character
- **Minimum lot sizes** may be larger (450m² vs 400m²)
- **Height limits** may be more restrictive

### Important: Not All Big Blocks Have Character Overlays

Many large blocks in Brisbane suburbs do NOT have character overlays. The overlay is specific to certain precincts — not all properties in a suburb are covered. Always check the council mapping tool.

## When the Land Is Worth More Than the House

### Signs Your Land Value Dominates

- Your house needs major structural work ($100,000+)
- The block is over 900m² with development zoning
- The house is not in a character overlay area
- Developers have approached you
- Neighbours' blocks have been developed

### Typical Value Breakdown

| Suburb | Total Value | House Value | Land Value |
|---|---|---|---|
| Paddington (character overlay, 800m²) | $1,500,000 | $400,000 | $1,100,000 |
| Red Hill (no overlay, 900m²) | $1,400,000 | $200,000 | $1,200,000 |
| Coorparoo (no overlay, 1,000m²) | $1,200,000 | $150,000 | $1,050,000 |

In areas without character overlays, the land almost always dominates, and a developer will pay for the land value (or more).

## When the Queenslander Adds Value

### Renovated Character Homes Command Premiums

A beautifully renovated Queenslander in a character area attracts a specific buyer pool willing to pay a premium for:
- Period architecture and craftsmanship
- High ceilings, timber floors, VJ walls
- Wrap-around verandahs
- Street appeal and neighbourhood character

A renovated Queenslander in Paddington might sell for $2.2M to a homebuyer — more than a developer would pay for the land alone.

### The Renovation Test

**Should you renovate and sell as a home, or sell as a development site?**

Ask: Will the renovated house sell for more than the development site value?

- If the house is in a **character overlay** and the block is under 900m²: probably yes — renovate and sell to a homebuyer
- If there is **no character overlay** and the block is over 800m²: probably no — sell to a developer
- If the house is **unrenovated** and the block is large: almost certainly sell as a development site

## Strategies for Maximum Value

### Strategy 1: Sell as a Development Site
Remove the house from the equation. Market the block to developers based on its zoning, size, and development potential. The Queenslander is demolished by the buyer.

### Strategy 2: Relocate the Queenslander
Queenslanders can be physically moved to another site. House relocation costs $50,000-$150,000 depending on size and distance. This allows you to sell the vacant land (worth more) and the buyer of the Queenslander gets a character home on their own block.

### Strategy 3: Retain and Subdivide
If the Queenslander is at the front of the block, keep it on a reduced lot and sell the rear portion. You preserve the character home and unlock land value from the back.

### Strategy 4: Renovate and Sell to a Homebuyer
If the character premium exceeds the development premium, invest in renovation and target the lifestyle buyer market.

## Making the Decision

The right strategy depends on three factors:
1. **Is there a character overlay?** Check council maps.
2. **What is the block size and zoning?** Larger blocks in higher-density zones favour development.
3. **What is the house condition?** Good condition favours renovation; poor condition favours development sale.

[Contact ACRES](/contact) for a free assessment that models all scenarios and recommends the strategy that maximises your outcome.
`,
    faq: [
      { q: 'Can I demolish a Queenslander in Brisbane?', a: 'If your property is in a Character Overlay area, demolition requires a formal assessment by council. Outside character areas, demolition is generally permitted with standard approvals. Always check your specific overlay status on the council mapping tool.' },
      { q: 'Is a Queenslander on a big block worth more as a home or a development site?', a: 'It depends on the character overlay, block size, and house condition. In character overlay areas, a renovated Queenslander may sell for more to a homebuyer. Outside character areas, the land value for development typically exceeds the whole-property residential value.' },
      { q: 'Can I relocate a Queenslander to another block?', a: 'Yes. Queenslanders are designed to be moved. Relocation costs $50,000-$150,000 depending on the size and distance. This can be a win-win: you sell the vacant land for development value and the house finds a new home.' },
    ],
  },
  {
    id: 'duplex-site-worth-more-development-opportunity',
    category: 'Seller Guide',
    title: 'Is Your Duplex Site Worth More as a Development Opportunity?',
    excerpt: 'Existing duplex sites often have untapped potential for higher-density development. Here is how to find out.',
    date: '2026-04-11',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1558036117-15d82a90b9b1?w=800&q=85',
    icon: Building2,
    metaDescription: 'Is your duplex site worth more as a development opportunity? How to assess whether your existing duplex block has higher-density potential and what developers would pay.',
    content: `
## Your Duplex Might Be Sitting on a Goldmine

If you own a duplex on a block over 600m², your property may be worth significantly more as a development site than as a rental investment. Many duplex sites were built decades ago under older planning rules — the current zoning may now permit substantially more dwellings.

## Why Duplex Sites Attract Developers

### Already Dual-Titled
Many duplexes sit on lots that are already subdivided into two titles. This means the site has established access, services, and a track record of dual occupancy — making further development a natural progression.

### Typically Larger Blocks
Duplexes were often built on blocks of 700-1,200m² — large enough for 3-6 townhouses under current planning schemes.

### Zoning Has Often Changed
A duplex built in the 1980s under Low Density zoning may now sit in a Low-Medium or Medium Density zone, permitting far more dwellings than the existing two.

### Ageing Buildings
Many duplexes from the 1970s-1990s are reaching the end of their economic life. Maintenance costs are rising, and the rental return on ageing buildings diminishes while the land value increases.

## How to Assess Your Duplex Site

### Step 1: Check Current Zoning
Your duplex may have been built under a zone that no longer exists. Check the current zone on your council's mapping tool.

### Step 2: Calculate Achievable Density

| Block Size | LMDR Zone Potential | MDR Zone Potential |
|---|---|---|
| 600m² | 2-3 dwellings | 4-6 dwellings |
| 800m² | 3-4 dwellings | 6-8 dwellings |
| 1,000m² | 4-5 dwellings | 8-12 dwellings |
| 1,200m² | 5-6 dwellings | 10-15 dwellings |

If the achievable density significantly exceeds your current 2 dwellings, the development value likely exceeds the rental investment value.

### Step 3: Compare Values

**Current value as a duplex investment:**
- Rental income: 2 x $450/week = $46,800/year
- At a 4% yield, investment value = $1,170,000

**Development site value (6 townhouses possible):**
- Developer pays: $1,400,000-$1,600,000

In this example, the development site value exceeds the investment value by $230,000-$430,000.

## When to Sell as a Development Site

- Your duplex is over 20 years old and maintenance costs are rising
- The zoning permits significantly more dwellings than you currently have
- Developer activity in your suburb is increasing
- You want to deploy your capital more efficiently
- The rental yield has dropped below 4%

## When to Hold as a Rental Investment

- The duplex is modern and low-maintenance
- Rental demand is strong and yields are above 5%
- The zoning does not permit significantly more density
- You value passive income over capital realisation

## Maximising the Sale

If you decide to sell, the key is marketing to the right buyer pool:

1. **Developers and builder-developers** — your primary market
2. **Investors who will redevelop later** — may pay a premium to hold rental income while planning
3. **Owner-occupiers** — if one unit is vacant, a buyer may live in one and rent the other while planning development

[Request a free duplex site assessment](/contact) to understand whether your property is worth more as a development opportunity.
`,
    faq: [
      { q: 'Is my duplex worth more as a development site?', a: 'If your block is over 600m² and the current zoning permits more than 2 dwellings, the development value likely exceeds the investment value. A development-focused appraisal will confirm the differential.' },
      { q: 'Can I sell a duplex to a developer while tenants are living there?', a: 'Yes. Most developer contracts allow existing tenancies to continue until the buyer is ready to develop. You may need to provide vacant possession at settlement, which means giving tenants proper notice under Queensland tenancy law.' },
      { q: 'Do developers buy duplexes?', a: 'Absolutely. Developers frequently target existing duplex sites because they are typically larger blocks in established suburbs with proven dual-occupancy potential. The existing buildings are demolished to make way for higher-density development.' },
    ],
  },
  {
    id: 'corner-block-why-developers-pay-premium',
    category: 'Seller Guide',
    title: 'Corner Block Owners: Why Developers Will Pay You a Premium',
    excerpt: 'Corner blocks are disproportionately valuable for development. Here is why and how much more you can expect.',
    date: '2026-04-10',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1592595896616-c37162298647?w=800&q=85',
    icon: MapPin,
    metaDescription: 'Why corner blocks are worth more to developers in Brisbane. Dual street access, subdivision advantages, and how much premium corner block owners can expect.',
    content: `
## The Corner Block Advantage

If you own a corner block, you hold one of the most sought-after site configurations in residential development. Developers consistently pay 10-25% more for corner blocks compared to equivalent mid-block properties. Here is why.

## Why Corner Blocks Are More Valuable

### 1. Dual Street Access
Corner blocks have frontage to two streets. This provides:
- **Two access points** — vehicles can enter from one street and exit another, or each dwelling can have its own street address
- **Easier subdivision** — each new lot can face a different street, eliminating the need for battle-axe driveways
- **Better emergency access** — meets fire safety requirements more easily

### 2. Greater Effective Frontage
A 600m² mid-block lot might have 15m of frontage. A 600m² corner block might have 15m on one street and 20m on the other — 35m of total frontage. More frontage = more design flexibility.

### 3. Better Natural Light
Corner blocks allow buildings to have windows on three sides instead of two, creating better natural light and ventilation. This makes the end product more attractive to buyers.

### 4. Reduced Neighbour Conflicts
With fewer adjoining neighbours, there are fewer overlooking, overshadowing, and privacy issues — which means fewer objections and smoother council approvals.

### 5. Higher End-Product Value
Townhouses and units on corner blocks sell for more because they have better street presence, light, and privacy. Higher end-product value means the developer can pay more for the land.

## The Corner Block Premium

| Block Size | Mid-Block Value | Corner Block Value | Premium |
|---|---|---|---|
| 600m² in Nundah | $650,000 | $730,000 | +12% |
| 800m² in Coorparoo | $850,000 | $1,020,000 | +20% |
| 1,000m² in Holland Park | $900,000 | $1,080,000 | +20% |
| 700m² in Chermside | $720,000 | $830,000 | +15% |

## What Developers Build on Corner Blocks

Corner blocks unlock specific development types:

### Side-by-Side Duplex
Two detached or semi-detached homes, each facing a different street. Clean, simple, and highly saleable.

### Wrap-Around Townhouses
Townhouses arranged along both street frontages with internal courtyard parking. Popular configuration for 4-6 dwelling projects.

### Small Apartment Building
Corner sites are ideal for walk-up apartments (3-4 storeys) because the dual frontage provides better light, ventilation, and visual amenity.

## Maximising Your Corner Block Value

1. **Get a development-focused appraisal** — standard residential valuations undervalue the corner premium
2. **Highlight the dual access** in marketing materials
3. **Provide a preliminary site plan** showing potential building layouts
4. **Market to developers specifically** — not just general buyers

A corner block that is marketed only to homebuyers will sell for the residential value. Marketed to developers, it captures the full corner premium.

## Is Your Corner Block a Development Site?

If your corner block is over 500m² and zoned Low-Medium Density or higher, it almost certainly has development potential. Even in Low Density zones, corner blocks can accommodate duplexes or two-lot subdivisions more efficiently than mid-block sites.

[Request a free corner block assessment](/contact) and we will provide a detailed analysis of your site's development potential and value.
`,
    faq: [
      { q: 'How much more is a corner block worth than a mid-block lot?', a: 'Corner blocks typically sell for 10-25% more than equivalent mid-block properties when marketed to developers. The premium reflects the design, access, and approval advantages that dual street frontage provides.' },
      { q: 'What is the minimum corner block size for development?', a: 'In most SEQ councils, corner blocks over 500m² have development potential. The dual street frontage makes subdivision and dual occupancy feasible on smaller sites than would work mid-block.' },
      { q: 'Do I need to tell the agent my block is a corner?', a: 'A quality agent will already know, but ensure your agent understands the development implications of corner blocks. An agent who markets your corner block only to homebuyers will not capture the full corner premium from developer buyers.' },
    ],
  },
  {
    id: 'acreage-subdivision-rural-land-urban-gold',
    category: 'Seller Guide',
    title: 'Acreage to Subdivision: When Rural Land Becomes Urban Gold',
    excerpt: 'Acreage properties on the edge of Brisbane urban areas are being rezoned and subdivided at record rates.',
    date: '2026-04-09',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800&q=85',
    icon: TrendingUp,
    metaDescription: 'How acreage land on Brisbane fringe is being rezoned for residential subdivision. Where acreage is becoming urban, what your land could be worth, and the subdivision process.',
    content: `
## The Urban Boundary Is Moving

Across South East Queensland, the line between rural and urban is shifting. Properties that were rural acreage five years ago are now being rezoned for residential subdivision. And the landowners who recognise this shift early are capturing extraordinary value.

## Where the Transition Is Happening

### Brisbane Fringe
- **Rochedale** — Former acreage being rezoned and subdivided into residential estates
- **Pallara / Heathwood** — Emerging community areas transitioning to low density residential
- **Wakerley** — Pockets of larger lots near Manly West being assessed for higher density

### Logan Growth Corridor
- **Park Ridge / Greenbank** — Massive residential land releases on former acreage
- **Yarrabilba** — From rural to master-planned community
- **Flagstone** — Similar trajectory to Yarrabilba

### Moreton Bay
- **Caboolture West** — Large acreage holdings being rezoned for residential
- **Narangba / Burpengary** — Semi-rural blocks subdividing into 10-40 lot estates
- **Samford Valley fringe** — Pressure mounting on larger lots near urban services

### Ipswich / Springfield
- **Ripley Valley** — One of Australia's largest land releases
- **Deebing Heights** — Acreage transitioning to residential
- **Walloon / Rosewood corridor** — Long-term growth area

### Sunshine Coast / Gold Coast
- **Palmview** — Former cane land becoming residential
- **Pimpama / Coomera** — Acreage to master-planned communities
- **Caloundra South** — Major residential expansion

## What Makes Acreage Ripe for Conversion?

### Key Indicators
1. **Inside the Urban Footprint** — the SEQ Regional Plan designates where urban development can occur
2. **Trunk infrastructure extending** — water, sewer, roads reaching your area
3. **Neighbouring properties being developed** — if blocks around you are being subdivided, yours is next
4. **Council rezoning** — zone changes from Rural or Rural Residential to Emerging Community or Residential
5. **New schools or shops being built** — community infrastructure follows population growth

## The Value Transformation

| Property Type | Rural Value | Residential Subdivision Value | Multiplier |
|---|---|---|---|
| 2 hectares (20,000m²) rural | $600,000-$1,000,000 | $3,000,000-$6,000,000 | 3-6x |
| 5 hectares (50,000m²) rural | $1,000,000-$2,000,000 | $8,000,000-$15,000,000 | 5-8x |
| 10 hectares (100,000m²) rural | $1,500,000-$3,000,000 | $15,000,000-$30,000,000 | 5-10x |

These figures are indicative and depend heavily on location, zoning, infrastructure availability, and market conditions. But the transformation from rural to residential consistently delivers the largest value multipliers in Australian property.

## The Subdivision Process for Acreage

### Phase 1: Planning Assessment (2-4 months)
- Zoning confirmation and development potential
- Environmental assessments (vegetation, waterways, koala habitat)
- Infrastructure capacity assessment
- Preliminary lot yield design

### Phase 2: Development Application (6-18 months)
- Formal application to council
- Engineering design for roads, drainage, services
- Environmental and traffic impact assessments
- Possible public notification and submissions

### Phase 3: Civil Works (6-12 months)
- Road construction
- Water, sewer, stormwater infrastructure
- Electrical and telecommunications
- Landscaping and public spaces

### Phase 4: Lot Sales (6-24 months)
- Title registration
- Marketing and sales
- Settlement

### Total Timeline: 2-5 years from start to completing sales

## Your Options as an Acreage Owner

### Option 1: Sell to a Developer/Land Bank
- Fast, clean exit
- Developer handles all approvals and construction
- You receive a lump sum (or staged payments)
- Best for: owners wanting certainty and minimal involvement

### Option 2: Obtain Approval Then Sell
- Get the Development Approval yourself
- Sell the approved site for a 20-40% premium
- You take the approval risk but capture the planning uplift
- Best for: patient owners with capital for the approval process

### Option 3: Develop Yourself
- Highest potential return
- Requires significant capital ($2M-$20M+ depending on scale)
- 3-5 year commitment
- Best for: experienced developers or those partnering with one

[Contact ACRES](/contact) to discuss your acreage property's potential. We work with landowners across the SEQ growth corridors.
`,
    faq: [
      { q: 'How do I know if my acreage can be subdivided?', a: 'Check if your property is within the SEQ Regional Plan urban footprint. If it is, and if trunk infrastructure (water, sewer, roads) is available or planned, your land likely has subdivision potential. A town planner can confirm the specific development capacity.' },
      { q: 'How long does it take to subdivide acreage into residential lots?', a: 'The full process from planning to selling individual lots typically takes 2-5 years. The timeline depends on the complexity of the site, council assessment timeframes, infrastructure requirements, and market conditions.' },
      { q: 'What is my acreage worth for development?', a: 'Development value depends on the achievable lot yield. As a rough guide, multiply the number of lots by the expected lot sale price, then subtract 40-50% for development costs and developer profit. The remainder is the approximate land value.' },
    ],
  },
  {
    id: 'selling-commercial-for-residential-development',
    category: 'Seller Guide',
    title: 'Selling a Commercial Property for Residential Development',
    excerpt: 'Commercial and industrial properties in residential-demand areas are being converted to housing. Is yours a candidate?',
    date: '2026-04-08',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=85',
    icon: Building2,
    metaDescription: 'How to sell a commercial or industrial property for residential development in Brisbane. When commercial-to-residential conversion makes sense and what developers will pay.',
    content: `
## Commercial to Residential: The Conversion Opportunity

Across Brisbane, commercial and light industrial properties in residential-demand areas are being acquired by developers for conversion to housing. If you own a commercial property in or near a residential suburb, it may be worth significantly more as a residential development site.

## Why This Is Happening

### Residential Demand Outstrips Commercial

Brisbane's population growth is creating intense demand for housing, while some commercial areas face increasing vacancy. The result: commercial land in residential areas is worth more for housing than for its current commercial use.

### Council Planning Supports Conversion

Many councils are actively encouraging commercial-to-residential conversion in appropriate areas through:
- Mixed-use zoning that permits residential above commercial
- Planning scheme amendments favouring housing near transport
- Relaxed requirements for residential development in transition areas

### Infrastructure Investment

New transport infrastructure (CRR, Metro) is making formerly commercial/industrial areas attractive for residential development. Suburbs like Albion, Bowen Hills, and Woolloongabba are seeing significant commercial-to-residential conversion.

## When Conversion Makes Sense

### Strong Indicators
- Your commercial property is within 2km of a residential suburb's centre
- Surrounding properties are already being converted or redeveloped
- Your area has been rezoned to mixed-use or medium density
- Commercial vacancy rates in your area are rising
- Residential property values nearby significantly exceed commercial values per m²

### Less Suitable
- Your property is in a dedicated industrial/commercial zone unlikely to change
- Contamination issues would require expensive remediation
- The location has poor residential amenity (noise, traffic, limited services)

## The Value Differential

Commercial-to-residential conversion often creates significant value uplift:

| Property Type | Commercial Value | Residential Dev Value | Uplift |
|---|---|---|---|
| Warehouse on 1,500m² (Albion) | $1,800,000 | $3,200,000 | +78% |
| Retail shop on 800m² (Nundah) | $900,000 | $1,500,000 | +67% |
| Office on 600m² (Toowong) | $1,200,000 | $1,800,000 | +50% |

## Key Considerations

### Contamination
Former industrial and commercial sites may have soil contamination requiring remediation. Environmental site assessments ($5,000-$20,000) should be conducted early. Clean sites command full development value; contaminated sites are discounted.

### Zoning Transition
If your property is still zoned commercial but the area is transitioning, a formal planning assessment can establish the residential development potential. Some councils will support a material change of use application from commercial to residential.

### Existing Leases
If your property has commercial tenants, lease terms affect timing. A property with a short-term lease or vacancy is more immediately attractive to residential developers.

## How to Sell for Maximum Value

1. **Get a residential development appraisal** — not a commercial appraisal
2. **Commission an environmental assessment** — clean sites sell faster
3. **Obtain a planning assessment** — confirming residential development potential
4. **Market to residential developers** — not commercial buyers
5. **Use a structured sale process** — create competition between developer buyers

[Contact ACRES](/contact) if you own a commercial property in a residential-demand area and want to understand its conversion potential.
`,
    faq: [
      { q: 'Can I convert my commercial property to residential?', a: 'It depends on the zoning. If your property is in a mixed-use zone or an area being rezoned for residential, conversion is feasible. A planning assessment will confirm whether residential development is permitted or could be approved through a development application.' },
      { q: 'Do developers buy commercial properties for residential development?', a: 'Yes, actively. In Brisbane suburbs transitioning from commercial to residential (Albion, Bowen Hills, Woolloongabba, Nundah), developers are paying residential development premiums for commercial sites. The value uplift can be 50-80% above commercial value.' },
      { q: 'What about contamination on commercial sites?', a: 'Environmental contamination is common on former commercial and industrial sites. An environmental site assessment identifies any contamination. Clean sites achieve full development value; contaminated sites are discounted by the estimated remediation cost.' },
    ],
  },

  // ═══════════════════════════════════════════
  // LIFE EVENT TRIGGERS (4 articles)
  // ═══════════════════════════════════════════
  {
    id: 'retiring-turn-big-block-into-retirement-fund',
    category: 'Seller Guide',
    title: 'Retiring? How to Turn Your Big Block Into Your Retirement Fund',
    excerpt: 'Your large block could fund a comfortable retirement. Here are your options for converting land into income or capital.',
    date: '2026-04-07',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1516733725897-1aa73b87c8e8?w=800&q=85',
    icon: Shield,
    metaDescription: 'How to use your large block to fund retirement. Subdivision, selling to a developer, granny flat income, and strategies for converting Brisbane property into retirement capital.',
    content: `
## Your Biggest Asset, Your Best Retirement Tool

For many Australians approaching retirement, their home is their largest asset — often worth more than their superannuation. And if that home sits on a large block, the land value alone could fund a decade or more of comfortable retirement.

The question is not whether your block has value. It is how to convert that value into retirement income or capital in the most tax-effective way.

## Option 1: Sell the Rear Lot

**Best for:** Retirees who want to stay in their home but need capital.

Subdivide the back portion of your block and sell it as a vacant lot. Keep living in your home on the reduced front lot.

**Financial example (Holland Park, 1,000m² block):**
- Subdivision cost: $100,000
- Rear lot sale: $450,000
- **Net capital released: $350,000**
- Invested at 5%: $17,500/year income

This $350,000 supplements your super and pension without requiring you to move.

### Tax Considerations
If the property is your primary residence, the rear lot sale may be partially or fully CGT-exempt depending on how the subdivision is structured. Seek tax advice before proceeding — the structuring can save you tens of thousands.

## Option 2: Build a Granny Flat and Rent It

**Best for:** Retirees who want ongoing income without selling land.

Build a self-contained dwelling in your backyard and rent it out.

**Financial example:**
- Build cost: $180,000
- Rental income: $450/week ($23,400/year)
- **Gross yield: 13%**
- Pays for itself in under 8 years

You retain full ownership of the land and gain a steady income stream. The granny flat also increases your overall property value.

## Option 3: Sell the Entire Block and Downsize

**Best for:** Retirees ready to move to something smaller, lower-maintenance.

Sell your large block to a developer (at a development premium) and purchase a smaller, modern home or apartment.

**Financial example (Chermside, 850m² block):**
- Sale to developer: $950,000
- Purchase new unit nearby: $550,000
- **Surplus capital: $400,000**
- Plus: lower maintenance, rates, and insurance

### Stamp Duty Concession
Queensland offers stamp duty concessions for eligible pensioners purchasing their new home. Check current thresholds with your solicitor.

### Centrelink Implications
The family home is exempt from the assets test for Age Pension. If you sell and retain significant capital, it may affect your pension eligibility. Plan the timing and structure carefully with your financial advisor.

## Option 4: Joint Venture with a Developer

**Best for:** Retirees who want maximum return and have time.

Contribute your land to a development joint venture. The developer builds townhouses or units; you share the profits without contributing capital.

**Potential return:** 30-40% of gross project value
**Timeline:** 2-4 years
**Risk:** Higher than other options — developer performance, market conditions, construction costs

This option can deliver the highest total return but carries more complexity and risk. Ensure robust legal protections.

## Tax Planning for Retirement Sales

### CGT on Your Home
Your primary residence is CGT-exempt. But:
- The rear lot (if subdivided) may attract CGT
- If you have rented part of the property, partial CGT may apply
- The 6-year absence rule may be relevant if you have moved out temporarily

### Super Contributions
Sale proceeds can potentially be contributed to super if you are eligible. The downsizer contribution allows up to $300,000 per person (or $600,000 per couple) to be contributed to super from the sale of your home, regardless of existing super balance or total super cap.

### Pension Planning
Coordinate the sale timing with your financial advisor to optimise Centrelink treatment and tax outcomes.

## Your Next Step

Every retirement situation is different. The first step is understanding what your block is worth and which strategy maximises your after-tax outcome.

[Book a free retirement property consultation](/contact) with ACRES. We work with your financial advisor and solicitor to ensure the best possible result.
`,
    faq: [
      { q: 'Can I sell part of my block and keep living in my home?', a: 'Yes. If your house is at the front of the block, you can subdivide the rear portion and sell it while continuing to live in your home. This is one of the most popular strategies for retirees with large blocks.' },
      { q: 'Will selling my property affect my Age Pension?', a: 'Potentially. While your home is exempt from the assets test, proceeds from selling (including downsizing capital) are assessed as financial assets. Plan the timing and structure carefully with your financial advisor and Centrelink.' },
      { q: 'What is the downsizer super contribution?', a: 'The downsizer contribution allows Australians 55+ to contribute up to $300,000 per person from the sale of their home into superannuation. It does not count towards existing contribution caps and is available regardless of your total super balance.' },
    ],
  },
  {
    id: 'downsizing-sell-developer-fund-next-chapter',
    category: 'Seller Guide',
    title: 'Downsizing in Brisbane: Sell to a Developer and Fund Your Next Chapter',
    excerpt: 'When it is time to downsize, selling your large block to a developer can give you significantly more capital than a standard sale.',
    date: '2026-04-06',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=85',
    icon: Home,
    metaDescription: 'How to downsize in Brisbane by selling your large block to a developer. Why developers pay more, how to maximise your sale price, and planning your next move.',
    content: `
## Downsizing? Your Block Is Your Negotiating Power

When the time comes to move from the family home to something more manageable, the way you sell matters enormously. A large block sold to a homebuyer fetches one price. The same block sold to a developer can fetch 20-50% more.

That difference could mean the gap between a modest unit and a premium apartment — or between scraping by and living comfortably.

## Why Developers Pay More

A homebuyer values your property as a single home on a single block. They compare it to other 3-bedroom houses in the suburb.

A developer values your property based on what they can build. A 900m² block that a homebuyer values at $850,000 might support 4 townhouses worth $3.2 million to a developer — and they will pay $1,050,000-$1,200,000 for the land.

| Buyer Type | Offer on 900m² Block (Mount Gravatt) |
|---|---|
| Homebuyer | $850,000-$900,000 |
| Developer (competitive process) | $1,050,000-$1,200,000 |
| **Difference** | **$150,000-$300,000** |

## The Downsizing Equation

**What you sell for - What you buy = Capital surplus**

| Scenario | Sale Price | New Purchase | Surplus |
|---|---|---|---|
| Standard sale to homebuyer | $900,000 | $600,000 (unit) | $300,000 |
| Development sale | $1,150,000 | $600,000 (unit) | $550,000 |

The development sale produces $250,000 more surplus capital. Invested at 5%, that is an additional $12,500/year in retirement income — every year.

## How to Maximise Your Development Sale

### 1. Get a Development-Focused Appraisal
Do not rely on a standard residential appraisal. You need an agent who understands development feasibility and can identify the true development value.

### 2. Create Competition
The worst outcome is negotiating one-on-one with a single developer. Use an EOI (Expressions of Interest) process to attract multiple buyers.

### 3. Time Your Settlement
Most developers are flexible on settlement timing. You can negotiate a longer settlement period (60-120 days) to give yourself time to find and purchase your next home.

### 4. Consider a Leaseback
Some developers will let you lease back your property for 6-12 months after settlement while they prepare their Development Application. This gives you time to downsize without pressure.

## Where to Downsize in Brisbane

Popular downsizing destinations for sellers of large suburban blocks:

- **Inner-city apartments** — Woolloongabba, South Brisbane, Kangaroo Point
- **Boutique developments** — Bulimba, Hawthorne, New Farm
- **Lifestyle communities** — over-55s communities in Moreton Bay, Redlands
- **Coastal** — Redcliffe, Scarborough, Gold Coast
- **Regional lifestyle** — Noosa, Sunshine Coast hinterland, Toowoomba

## The Emotional Side

Leaving the family home is emotional. Give yourself permission to feel that while also recognising the practical benefits:
- Less maintenance and upkeep
- Lower costs (rates, insurance, repairs)
- More capital for travel, hobbies, family
- A home that suits your current needs, not your needs from 20 years ago

## Start the Conversation

There is no obligation and no pressure. [Contact ACRES](/contact) for a confidential chat about your options. We will provide a free development assessment and help you understand what your block is worth to a developer.
`,
    faq: [
      { q: 'How much more will a developer pay than a homebuyer?', a: 'Developers typically pay 20-50% more than homebuyers for large blocks with development potential. The exact premium depends on the block size, zoning, and what can be built. A structured sale process with multiple developer buyers maximises the premium.' },
      { q: 'Can I stay in my home after selling to a developer?', a: 'Often yes. Many developers will agree to a leaseback arrangement where you remain in the property for 6-12 months after settlement while they prepare their Development Application. This gives you time to find and settle into your new home.' },
      { q: 'What costs are involved in downsizing?', a: 'Key costs include agent commission on the sale (2-3%), conveyancing on both sale and purchase ($3,000-$6,000), stamp duty on the new purchase (check for pensioner concessions), and moving costs ($1,000-$3,000).' },
    ],
  },
  {
    id: 'inherited-property-development-site',
    category: 'Seller Guide',
    title: "Inherited a Property? Here's How to Know If It's a Development Site",
    excerpt: 'Many inherited properties sit on large blocks with significant development value that executors and beneficiaries overlook.',
    date: '2026-04-05',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1480074568708-e7b720bb3f09?w=800&q=85',
    icon: FileCheck,
    metaDescription: 'How to assess if an inherited property is a development site. Guide for executors and beneficiaries on identifying development potential, CGT implications, and maximising the estate value.',
    content: `
## The Development Value Most Executors Miss

When someone inherits a property, the first instinct is usually to get a standard real estate appraisal and sell. But if the inherited property sits on a large block — and many older properties do — this approach can leave hundreds of thousands of dollars on the table.

Properties owned by elderly family members were often purchased decades ago when large blocks were standard. A house that was worth $250,000 when it was bought in 1990 might now sit on land worth $1.2 million for development.

## Red Flags That Suggest Development Value

### The Property Characteristics
- Block is over 600m² (check the title or rates notice)
- The house is old, modest, or in poor condition
- The property has a large, underused backyard
- It is on a corner or has dual street frontage
- The suburb has changed significantly since the property was purchased

### The Neighbourhood Clues
- Neighbouring properties have been demolished and rebuilt
- Townhouses or apartments are appearing in the street
- Developer letterbox drops are common in the area
- The suburb is near new transport infrastructure

## The Value Difference

| Scenario | Standard Appraisal | Development Appraisal |
|---|---|---|
| 3-bed house on 850m², Stafford | $780,000 | $1,050,000 |
| 2-bed cottage on 1,000m², Moorooka | $700,000 | $1,000,000 |
| 4-bed house on 750m², Greenslopes | $950,000 | $1,250,000 |

In each case, the development appraisal reveals $200,000-$300,000 in additional value that a standard residential appraisal misses.

## CGT Implications for Inherited Properties

### Pre-20 September 1985 Purchase
If the deceased purchased the property before this date, the beneficiaries' cost base is the market value at the date of death. Any gain from death to sale is taxable (unless sold within 2 years as the deceased's main residence).

### Post-20 September 1985 Purchase
The original purchase price becomes the cost base. Any capital gain from purchase to sale is taxable, but the 50% CGT discount applies if held for 12+ months.

### The 2-Year Main Residence Rule
If the property was the deceased's main residence, executors have 2 years from the date of death to sell it CGT-free. After 2 years, the market value at the date of death becomes the cost base.

**Critical tip:** If the property has significant development value, selling within the 2-year window can save the estate a substantial CGT bill.

## What Executors Should Do

1. **Get TWO appraisals** — one standard residential and one development-focused
2. **Check the zoning** — even a basic zoning check can reveal development potential
3. **Consider timing** — the 2-year CGT exemption window is critical for estate planning
4. **Engage a development-savvy agent** — not just the cheapest or most convenient agent
5. **Communicate with beneficiaries** — explain the development value opportunity

## Handling Multiple Beneficiaries

When multiple beneficiaries inherit a property with development potential, align early on:
- Whether to sell as a development site or residential property
- Whether to invest in obtaining a DA before selling (adds value but takes time)
- How to split proceeds (usually per the will or equally)
- Timeline expectations (development sales can take longer)

[Contact ACRES](/contact) for a confidential deceased estate development assessment. We handle these situations with sensitivity and discretion.
`,
    faq: [
      { q: 'How do I know if an inherited property is a development site?', a: 'Check the block size (over 600m² is a strong indicator), the zoning (Low-Medium Density or higher), and the neighbourhood (developers active in the suburb). Then get a development-focused appraisal to confirm the value difference.' },
      { q: 'Is there CGT on inherited property?', a: 'It depends on when the property was purchased and when it is sold. If the deceased main residence is sold within 2 years of death, it is CGT-exempt. Investment properties and sales after 2 years may attract CGT.' },
      { q: 'Should I renovate an inherited property before selling?', a: 'If the block has significant development value, renovation is usually a waste of money — developers will demolish the house regardless. Spend the renovation budget on a development-focused appraisal and marketing campaign instead.' },
    ],
  },
  {
    id: 'divorce-property-settlement-development-site-value',
    category: 'Seller Guide',
    title: 'Divorce Property Settlement: Is Your Block Worth More as a Development Site?',
    excerpt: 'During property settlement, understanding development value can significantly change the equation for both parties.',
    date: '2026-04-04',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=85',
    icon: Scale,
    metaDescription: 'How development site value affects divorce property settlements. Why standard valuations may understate your property worth and how to ensure a fair outcome.',
    content: `
## Development Value Changes the Property Settlement Equation

In divorce property settlements, the family home is usually the largest asset. If that home sits on a large block, a standard residential valuation may significantly understate its true market value — and one party may inadvertently receive less than their fair share.

## The Problem with Standard Valuations

Family law property settlements typically rely on sworn valuations from licensed valuers. These valuations assess the property as a single residential dwelling — which is correct for mortgage purposes but may not reflect what a developer would pay.

**Example:**
- Sworn valuation (single dwelling): $950,000
- Development site value (4-townhouse potential): $1,250,000
- **Understated by: $300,000**

If the property is being sold as part of the settlement, and only one party knows about the development potential, the other party loses out.

## When Development Value Matters

### It Matters If:
- The block is over 600m² in a suburb with developer activity
- The property is going to be sold (not retained by one party)
- The valuation seems low compared to recent sales of similar blocks
- You have received developer interest or letterbox cards
- Neighbouring properties have been developed

### It May Not Matter If:
- The block is small (under 500m²) with no subdivision potential
- One party is retaining the property as their home
- The property is in a character/heritage area with restricted development
- Both parties agree on the value and sale approach

## Protecting Your Interests

### Request a Development-Focused Appraisal
In addition to the sworn valuation, obtain an appraisal that considers the property's highest and best use. This is particularly important if:
- You suspect development potential exists
- The other party has received developer interest
- You want to ensure the settlement is based on true market value

### Ensure the Sale Process Captures Full Value
If the property is to be sold, the sale method matters:
- **Standard listing to homebuyers:** captures residential value only
- **Targeted marketing to developers:** captures the development premium
- **EOI process:** creates competition and maximises price

Insist that the appointed agent markets the property to developers (not just homebuyers) if the block has development potential.

### Consider Independent Advice
Engage a development-focused property advisor to review the valuation and advise on the likely development value. This is separate from your family lawyer and provides a second opinion on the property's true worth.

## The CGT Advantage in Divorce

Property transfers between spouses as part of a court-approved settlement are CGT-exempt (rollover relief). This means:
- No CGT is payable at the time of transfer
- The receiving party inherits the original cost base
- CGT becomes payable when the receiving party eventually sells

If one party retains the property and later sells to a developer, the development premium is captured but CGT applies based on the original purchase price.

## Fair Outcomes for Both Parties

The goal is not to advantage one party over the other — it is to ensure both parties understand the property's true value so the settlement is fair.

If your property is being assessed as part of a divorce settlement and you believe it may have development potential, [contact ACRES](/contact) for a confidential development value assessment.
`,
    faq: [
      { q: 'Can development value affect my property settlement?', a: 'Yes, significantly. If the family home sits on a large block with development potential, a standard valuation may understate its true market value by $100,000-$500,000. Understanding this value ensures a fair settlement for both parties.' },
      { q: 'Should I get a development appraisal for my divorce settlement?', a: 'If your block is over 600m² in a suburb with developer activity, yes. A development-focused appraisal provides a more complete picture of the property value. This is particularly important if the property will be sold as part of the settlement.' },
      { q: 'Is there CGT on property transferred in divorce?', a: 'No. Property transfers between spouses as part of a Family Court-approved property settlement receive CGT rollover relief. No CGT is payable at the time of transfer. CGT applies when the receiving party eventually sells the property.' },
    ],
  },

  // ═══════════════════════════════════════════
  // PROCESS / FEAR REDUCTION (4 articles)
  // ═══════════════════════════════════════════
  {
    id: 'what-happens-after-developer-buys-property',
    category: 'Seller Guide',
    title: 'What Happens After a Developer Buys Your Property?',
    excerpt: 'Many sellers worry about what happens to their home and street after the sale. Here is the reality.',
    date: '2026-04-03',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=85',
    icon: HelpCircle,
    metaDescription: 'What happens after a developer buys your property. The development timeline, what to expect as a seller, and how the process works from settlement to construction.',
    content: `
## After Settlement: The Developer's Timeline

One of the most common concerns we hear from sellers is: "What happens to my home after I sell?" Here is the typical timeline and what you can expect.

## The Standard Development Timeline

### Months 1-3: Planning and Design
After settlement, the developer engages architects and town planners to design the new development. Your house may sit vacant during this period. Some developers will rent the property short-term while planning proceeds.

### Months 3-12: Development Application
The developer lodges a formal Development Application with council. This is assessed over 3-6 months. If the development is code assessable, neighbours are not notified. If impact assessable, public notification occurs and neighbours can make submissions.

### Months 12-14: Demolition
Once approvals are in place, the existing house is demolished. This typically takes 1-2 weeks. Asbestos removal (common in pre-1990 homes) is handled by licensed specialists.

### Months 14-26: Construction
The new development is built. Construction of 3-6 townhouses typically takes 10-14 months. Larger projects take longer. During construction, there will be:
- Increased traffic from construction vehicles
- Noise during standard construction hours (6:30am-6:30pm weekdays)
- Temporary hoarding and site fencing
- Dust and vibration management

### Months 26-30: Completion and Sale
New dwellings are completed, landscaped, and sold or rented to new occupants.

## What This Means for You (the Seller)

### You Are Not Responsible for Anything Post-Settlement
Once settlement occurs and you receive your funds, you have no further obligations. The property belongs to the developer, and all planning, construction, and sales are their responsibility.

### Your Neighbours May Have Questions
Neighbours sometimes contact previous owners about development plans. You can simply direct them to council's development application register or to the developer's agent.

### The Street Changes — But Usually for the Better
Modern townhouse and unit developments in Brisbane are generally high-quality, well-designed, and improve the streetscape compared to ageing original homes. New landscaping, fencing, and driveways lift the presentation of the street.

## Common Concerns Addressed

### "Will my neighbours be angry?"
Some neighbours may have concerns about construction noise and traffic. But most understand that development is a natural part of suburban evolution. The new residents become part of the community.

### "Will it reduce neighbouring property values?"
Research consistently shows that well-designed medium-density development in established suburbs increases surrounding property values over time. The new residents add population, which supports local shops, schools, and services.

### "Can I control what gets built?"
No. Once you sell, the buyer determines what they build (subject to council approval). If this concerns you, discuss it with your agent — some sellers prefer buyers who plan sympathetic development.

### "How long until something is built?"
Typically 18-30 months from settlement to completion. Some developers land-bank (hold without developing) for longer, but this is less common in active markets.

## Making Peace with the Decision

Selling your home — especially one you have lived in for decades — is emotional. It helps to remember:
- Your home served you well, and now the land will serve a new purpose
- The new development will house families and individuals who need homes
- The financial benefits of selling at development value are substantial
- Your memories are not in the building — they are with you

[Talk to us](/contact) if you have any questions about what happens after you sell. We are happy to walk you through the process.
`,
    faq: [
      { q: 'How long after I sell will my house be demolished?', a: 'Typically 12-18 months after settlement. The developer needs to complete planning and obtain council approval before demolition can occur. Some developers demolish sooner if they have existing approvals.' },
      { q: 'Will I be notified about what gets built on my old property?', a: 'Not automatically. Once you sell, you are no longer a stakeholder. However, you can check the council development application register to see what has been proposed for the site.' },
      { q: 'Can I include conditions in the sale about what gets built?', a: 'This is unusual and most developers would not agree to design restrictions from a previous owner. However, you can factor the buyer development plans into your decision when choosing between offers.' },
    ],
  },
  {
    id: 'development-site-sales-myths-cost-sellers',
    category: 'Seller Guide',
    title: 'Development Site Sales: 7 Myths That Cost Sellers Money',
    excerpt: 'Misconceptions about selling to developers are costing property owners hundreds of thousands of dollars. Here is the truth.',
    date: '2026-04-02',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=85',
    icon: Shield,
    metaDescription: 'Seven myths about selling property to developers that cost sellers money. Common misconceptions debunked with facts about development site sales in Brisbane.',
    content: `
## Myths That Cost Sellers Real Money

Misinformation about development site sales causes property owners to accept lower prices, choose the wrong sale method, or avoid selling to developers altogether. Here are the seven most common myths — and the reality.

## Myth 1: "Developers Always Low-Ball"

**Reality:** Developers offer based on their feasibility numbers. A single developer with no competition WILL low-ball. But when multiple developers compete through a structured EOI process, offers regularly exceed the seller's expectations.

The problem is not developers — it is the absence of competition. An experienced agent creates that competition.

## Myth 2: "I Should Wait for the Market to Peak"

**Reality:** Nobody can time the market peak. What you can do is sell when:
- Demand for your block type is strong
- Listings of similar sites are low
- Interest rates allow developers to fund projects
- End-buyer confidence is high

All of these conditions currently exist in Brisbane (2026). Waiting for a "better" time risks missing the current window.

## Myth 3: "Any Real Estate Agent Can Sell a Development Site"

**Reality:** Selling a development site requires fundamentally different skills from selling a house:
- Understanding feasibility and development economics
- Access to a database of active developer buyers
- Ability to run competitive sale processes (EOI, tender)
- Knowledge of conditional contracts, put-and-call options, and DA conditions
- Planning expertise to present the site's potential

A residential agent will list your development site on realestate.com.au alongside family homes. A development-focused agent markets it directly to qualified developer buyers through targeted channels.

**The difference can be $100,000-$500,000.**

## Myth 4: "The Developer Will Make Millions — I'm Being Ripped Off"

**Reality:** Development is a high-risk business. Developers invest millions, wait years for returns, and face construction cost blowouts, market downturns, and planning refusals. Their profit margin (15-25%) compensates for this risk.

The key is ensuring you receive a fair share of the value through competition and professional negotiation — not resenting the developer's profit.

## Myth 5: "I Need to Get Council Approval Before Selling"

**Reality:** Getting a DA before selling can add 15-30% to your price, but it costs $30,000-$80,000 and takes 6-12 months. For many sellers, the time and capital are not justified.

Selling without a DA is standard practice. The developer takes on the approval risk — and prices their offer accordingly. If you want to capture some of the DA premium without the full commitment, a pre-lodgement assessment from council ($2,000-$5,000) provides indicative support that lifts buyer confidence.

## Myth 6: "Only Big Blocks Attract Developers"

**Reality:** Developers in Brisbane actively seek blocks from 600m² upward. In higher-density zones, blocks as small as 450m² can support a duplex or small townhouse development. Corner blocks and blocks near transport are valuable at even smaller sizes.

Do not assume your block is too small without checking.

## Myth 7: "Selling to a Developer Means My Neighbours Will Hate Me"

**Reality:** Your neighbours are adults. They understand that property ownership includes the right to sell. Many of them may be considering the same thing.

Modern development in Brisbane is generally well-designed and regulated. The new dwellings will house people who become part of the community. And the development of one block often increases the value of neighbouring properties by demonstrating development potential.

## The Bottom Line

These myths collectively cost Brisbane property owners millions of dollars every year. The antidote is information, professional advice, and a competitive sale process.

If you own a block over 600m² and have been holding back because of any of these myths, [contact ACRES](/contact) for a free, obligation-free assessment. The numbers may surprise you.
`,
    faq: [
      { q: 'Do developers always offer low prices?', a: 'No. A single developer approaching you directly will try to negotiate a lower price. But when multiple developers compete through a structured sale process, offers often exceed expectations. The key is creating competition, which an experienced agent facilitates.' },
      { q: 'Is it risky to sell to a developer?', a: 'No more risky than any property sale, provided you have good legal representation. Risks like conditional contracts and delayed settlement are managed through appropriate contract conditions, deposits, and sunset clauses.' },
      { q: 'What is the minimum block size developers want?', a: 'In Brisbane, developers actively seek blocks from 600m² upward. In higher-density zones or corner positions, blocks as small as 450m² can attract developer interest. Do not assume your block is too small without checking.' },
    ],
  },
  {
    id: 'how-long-stay-home-after-selling-developer',
    category: 'Seller Guide',
    title: 'How Long Can I Stay in My Home After Selling to a Developer?',
    excerpt: 'You do not have to move out immediately. Here is how settlement timing and leasebacks work with developer sales.',
    date: '2026-04-01',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=85',
    icon: Clock,
    metaDescription: 'How long you can stay in your home after selling to a developer. Settlement timing, leaseback arrangements, and how to negotiate occupancy terms in Brisbane.',
    content: `
## You Have More Time Than You Think

One of the biggest fears about selling to a developer is being forced to move quickly. In reality, you have significant control over the timeline — and several options for staying in your home longer than you might expect.

## Standard Settlement Periods

Like any property sale, the settlement period is negotiated in the contract:

| Settlement Type | Typical Period |
|---|---|
| Standard residential | 30-60 days |
| Development site (unconditional) | 30-90 days |
| Development site (conditional on DA) | 6-18 months |

For conditional contracts, you continue living in your home for the entire conditional period while the developer seeks council approval. This can be 12-18 months from contract signing.

## The Leaseback Option

A leaseback is an arrangement where you sell the property but lease it back from the developer for an agreed period. This is common in development site sales.

### How It Works
1. You sell the property and receive payment at settlement
2. You lease the property back from the developer at a nominal or market rent
3. You occupy the property for the agreed period (typically 3-12 months)
4. At the end of the lease, you vacate and the developer proceeds

### Typical Leaseback Terms
- **Duration:** 3-12 months (negotiate based on your needs)
- **Rent:** $0 to market rent (depends on negotiation — some developers offer rent-free periods to secure the deal)
- **Maintenance:** You maintain the property during the leaseback
- **Notice:** Clear end date in the lease agreement

### Why Developers Agree to Leasebacks
Developers typically need 6-12 months after settlement to complete planning and obtain approvals before they can demolish and build. During this time, having a tenant (you) maintaining the property is preferable to leaving it vacant.

## Extended Settlement

An alternative to a leaseback is simply negotiating a longer settlement period:

- **90 days:** Standard for many development sales
- **120-180 days:** Achievable with most developers
- **6-12 months:** Possible with conditional contracts

During extended settlement, you remain the owner and occupier. The developer pays a deposit (typically 5-10%) and commits to purchasing at the agreed date.

## Negotiating Your Timeline

### What You Can Ask For
- Settlement in 90-120 days (standard for development sales)
- Leaseback of 6-12 months after settlement
- Right to remain until you find and settle on your next home
- Break clause allowing you to vacate early if you find a home sooner

### What Developers Will Likely Accept
Most developers are flexible on timing because they cannot demolish and build immediately anyway. As long as the total period aligns with their project timeline, they will accommodate your needs.

### What Is Less Common
- Leasebacks longer than 12 months (developers want to start within 18 months of purchase)
- Open-ended occupation with no defined end date
- Leasebacks at zero rent for extended periods

## Planning Your Move

### Start Looking Early
Begin researching your next home as soon as you sign the contract. This gives you maximum time to find the right place.

### Coordinate Settlements
If possible, align the sale settlement with the purchase of your new home. Your solicitor can coordinate timing to minimise the gap between moving out and moving in.

### Temporary Accommodation
If there is a gap, short-term rental accommodation is available across Brisbane. Budget $500-$800/week for a furnished 2-bedroom apartment while you wait.

## The Key Takeaway

You are in control. Developer sales are negotiations, and timeline is one of the most flexible terms. A good agent will ensure your occupancy needs are built into the deal from the start.

[Contact ACRES](/contact) to discuss how we structure developer sales to protect your timeline and living arrangements.
`,
    faq: [
      { q: 'Do I have to move out immediately when I sell to a developer?', a: 'No. You negotiate the settlement period (typically 30-180 days) and can also arrange a leaseback for an additional 3-12 months. Most sellers have 6-18 months from signing the contract to vacating the property.' },
      { q: 'What is a leaseback arrangement?', a: 'A leaseback is where you sell the property but lease it back from the buyer for an agreed period. You continue living in the home after settlement while paying nominal or market rent. It is common in development site sales.' },
      { q: 'Will the developer charge me rent during a leaseback?', a: 'It depends on negotiation. Some developers offer rent-free leasebacks to secure the deal. Others charge a market or reduced rent. The terms are negotiated as part of the overall sale agreement.' },
    ],
  },
  {
    id: 'understanding-put-call-options-plain-english',
    category: 'Seller Guide',
    title: 'Understanding Put & Call Options: A Plain-English Guide for Property Owners',
    excerpt: 'Developers often propose put and call options instead of standard contracts. Here is what that means and how to protect yourself.',
    date: '2026-03-30',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1554224155-1696413565d3?w=800&q=85',
    icon: FileCheck,
    metaDescription: 'What are put and call options in property? Plain-English guide for property owners explaining how these contracts work, their benefits and risks, and how to protect yourself.',
    content: `
## Options Explained Simply

Put and call options are a common contract structure in development site sales. They sound complicated, but the concept is straightforward. This guide explains them in plain English so you can make informed decisions.

## What Is an Option?

An option gives one party the **right** (but not the obligation) to buy or sell a property at an agreed price within an agreed timeframe. The property owner grants this right in exchange for an upfront fee.

## The Two Types

### Call Option (Developer's Right to Buy)

The developer pays you a fee (the "option fee") for the right to purchase your property at an agreed price within a set period. If they exercise the option, the sale proceeds. If they do not, you keep the option fee and the property.

**In simple terms:** The developer is paying for the right to decide later. You get money now, and you might sell later.

### Put Option (Your Right to Sell)

You have the right to force the developer to buy your property at the agreed price. This protects you if the developer gets cold feet.

**In simple terms:** You can make the developer buy if they try to walk away.

### Put AND Call Option (Most Common)

Both options exist in the same agreement:
- The developer CAN buy (call option)
- You CAN force the sale (put option)
- The price and timeframe are agreed upfront

This is the most balanced structure and is standard in development site sales.

## Why Developers Use Options

### Planning Approval Risk
Developers often want to secure your property before they have council approval. The option period gives them time to:
1. Lodge a Development Application
2. Obtain council approval
3. Arrange finance based on the approved plans
4. Exercise the option and proceed to settlement

Without options, the developer would need to buy the property outright before knowing if council will approve their plans — a much higher risk.

### Stamp Duty Savings
In Queensland, option fees attract lower stamp duty than full property transfers. This saves the developer money, which can translate to a higher offer for your property.

## Key Terms to Understand

### Option Fee
The amount the developer pays upfront for the option. Typically 1-5% of the purchase price. This should be **non-refundable** — you keep it regardless of whether the sale proceeds.

### Option Period
How long the developer has to exercise the option. Typically 6-18 months. Shorter is better for you; longer gives the developer more time for approvals.

### Exercise Price
The price at which the developer will buy the property if they exercise the option. This is agreed at the start and does not change during the option period (unless the agreement includes escalation clauses).

### Exercise Event
What triggers the option being exercised. Usually the developer's decision after obtaining council approval and finance.

## Protecting Yourself

### Insist on a Substantial, Non-Refundable Option Fee
The option fee compensates you for taking the property off the market. If the developer does not proceed, you keep the fee. A higher fee gives the developer more incentive to follow through.

**Guideline:** 2-5% of the purchase price. On a $1M property, the option fee should be $20,000-$50,000.

### Keep the Option Period Reasonable
Shorter periods (6-12 months) keep pressure on the developer to act. Long periods (18-24 months) tie up your property with uncertain outcomes.

### Include a Put Option
Ensure you have the right to force the sale if the developer obtains approval but drags their feet. The put option protects you from being strung along.

### Get Independent Legal Advice
Option agreements are complex legal documents. Engage a solicitor experienced in development site transactions to review the terms before you sign.

### Continue Living in the Property
During the option period, you typically remain in the property. The agreement should specify that your occupancy continues undisturbed until settlement.

## When to Accept an Option Structure

**Accept if:**
- The option fee is substantial and non-refundable
- The option period is 6-12 months
- The exercise price reflects full development value
- You have a put option for protection
- Your solicitor has reviewed and approved the terms

**Decline if:**
- The option fee is minimal or refundable
- The option period is longer than 18 months
- The exercise price is below market value
- There is no put option
- The developer will not agree to reasonable terms

[Contact ACRES](/contact) if you have been offered an option agreement and want an independent assessment of the terms.
`,
    faq: [
      { q: 'What is a put and call option in property?', a: 'A put and call option is a contract structure where the developer pays a fee for the right to buy your property (call option) at an agreed price within a set timeframe. The put option gives you the right to force the developer to buy if they try to walk away.' },
      { q: 'Is the option fee refundable?', a: 'It should not be. Insist that the option fee is non-refundable — you keep it regardless of whether the developer exercises the option. This compensates you for taking the property off the market and gives the developer incentive to proceed.' },
      { q: 'How much should the option fee be?', a: 'A reasonable option fee is 2-5% of the agreed purchase price. On a $1,000,000 property, this means $20,000-$50,000 paid upfront and non-refundable. Lower fees do not adequately compensate you for the market risk during the option period.' },
    ],
  },
];
